import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Eye, Edit, Trash2, Filter, CheckSquare, Square, ClipboardList, Truck, CreditCard, CheckCircle, XCircle, User, CircleDot, Plus, ChevronDown, ChevronRight, Users, Copy, Building, Upload, Download, FileDown, ArrowUpDown, ArrowUp, ArrowDown, Columns, RotateCcw, Archive, ArchiveRestore, Settings, GitCommitHorizontal, Hash } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../../components/ui/dropdown-menu';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import RoundTripBadge from '../../components/ui-kit/RoundTripBadge';
import { getStatusBadge as getStatusConfig } from '../../constants/statuses';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import CSVImportModal from '../../components/CSVImportModal';
import CustomExportModal from '../../components/operations/CustomExportModal';
import BulkActionsBar from '../../components/BulkActionsBar';
import OperatorAssignmentModal from '../../components/OperatorAssignmentModal';
import { StatusBadge } from '../../components/StatusBadge';
import AdminCreateOperationModal from '../../components/AdminCreateOperationModal';
import AdvancedSearchPanel from '../../components/AdvancedSearchPanel';
import { FinancialStatusBadge } from '../../components/FinancialStatusBadge';
import { FINANCIAL_STATUS_CONFIG, FINANCIAL_STATUS_WORKFLOW } from '../../constants/financialStatus';
import { downloadExport, downloadExportPost } from '../../utils/exportDownload';
import MissingExternalRefAlert from '../../components/admin/MissingExternalRefAlert';

const token = 'cookie-auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════════
// Source de vérité unique : phase d'une opération dans le pipeline
// ═══════════════════════════════════════════════════════════════════
// Règle métier (23/04/2026) :
//   - Préparation : statuts de cycle devis / avant planification
//   - Exécution   : opération en cours SANS facture émise
//   - Facturation : statuts financiers en_attente_paiement/payee
//                   OU opération livrée avec facture émise non payée
//   - Terminées   : statut terminee/completed OU livrée + paid
//   - cancelled   : reste en "cancelled" (géré séparément)
// Cette fonction est utilisée à la fois pour les stats du pipeline
// et pour le filtre de la liste — garantit la cohérence des compteurs.
const PHASE_PREPARATION_STATUSES = [
  'demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client',
  'en_negociation', 'devis_accepte', 'pending',
];
const PHASE_EXECUTION_STATUSES = [
  'planifiee', 'prete_au_chargement', 'en_cours_de_chargement',
  'charge_en_route', 'accepted', 'in_progress',
];
const FINSTATUS_UNPAID_WITH_INVOICE = ['invoiced', 'pending_payment', 'partially_paid'];

const phaseOfOp = (op) => {
  if (!op) return null;
  const s = op.status;
  // Terminées : statut explicite OU livrée + paid
  if (s === 'terminee' || s === 'completed') return 'terminee';
  if (s === 'livree' && op.financialStatus === 'paid') return 'terminee';
  // Facturation : statuts dédiés OU livrée + facture émise non payée
  if (s === 'en_attente_paiement' || s === 'payee') return 'facturation';
  if (s === 'livree' && op.invoiceNumber && FINSTATUS_UNPAID_WITH_INVOICE.includes(op.financialStatus)) {
    return 'facturation';
  }
  // Exécution : tout ce qui est en cours, y compris "livree" sans facture encore émise
  if (PHASE_EXECUTION_STATUSES.includes(s)) return 'execution';
  if (s === 'livree') return 'execution';
  // Préparation
  if (PHASE_PREPARATION_STATUSES.includes(s)) return 'preparation';
  return null;
};

// Inline qualifier: shown when op has no financialStatus. Quick set via canonical workflow.
const InlineQualifyStatus = ({ op, onQualified }) => {
  const [saving, setSaving] = React.useState(false);
  const handleSet = async (newStatus) => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/operations/${op.id}/qualify-financial-status`, { financialStatus: newStatus });
      toast({ title: 'Statut financier qualifié', description: `${op.operationNumber || op.id} → ${FINANCIAL_STATUS_CONFIG[newStatus]?.label || newStatus}` });
      onQualified && onQualified(op.id, newStatus);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de qualifier', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={saving}
          data-testid={`qualify-status-trigger-${op.id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100 border border-dashed border-rose-300 transition-colors disabled:opacity-50"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
          {saving ? '…' : 'Qualifier'}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuLabel className="text-xs">Définir le statut</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {FINANCIAL_STATUS_WORKFLOW.map((s) => (
          <DropdownMenuItem
            key={s}
            onClick={() => handleSet(s)}
            className="text-xs cursor-pointer"
            data-testid={`qualify-status-option-${s}-${op.id}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${FINANCIAL_STATUS_CONFIG[s].dot}`} />
            {FINANCIAL_STATUS_CONFIG[s].label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OperationsManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  // RBAC V2 — gating des actions Opérations par permission (admin = bypass via hasPermission)
  const canCreate = hasPermission('operations.create');
  const canExport = hasPermission('operations.export');
  const canBulkImport = hasPermission('operations.bulk_import');
  const canDelete = hasPermission('operations.delete');
  const [operations, setOperations] = useState([]);
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [routeTypeFilter, setRouteTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [financialStatusFilter, setFinancialStatusFilter] = useState('all');
  const [divergenceFilter, setDivergenceFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showRefImport, setShowRefImport] = useState(false);
  const [showCustomExport, setShowCustomExport] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Column visibility state
  const ALL_COLUMNS = [
    { key: 'reference', label: 'Référence' },
    { key: 'externalRef', label: 'Réf. externe / N° dossier' },
    { key: 'client', label: 'Client' },
    { key: 'entreprise', label: 'Entreprise' },
    { key: 'trajet', label: 'Trajet' },
    { key: 'distance', label: 'Distance' },
    { key: 'date', label: 'Date' },
    { key: 'poids', label: 'Poids' },
    { key: 'prix', label: 'Prix' },
    { key: 'statut', label: 'Statut' },
    { key: 'statutFinancier', label: 'Statut financier' },
    { key: 'operateur', label: 'Opérateur' },
    { key: 'commercial', label: 'Commercial' },
    { key: 'transporteur', label: 'Transporteur' },
    { key: 'tarifTransport', label: 'Tarif Transport' },
    { key: 'marge', label: 'Marge' },
  ];
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const isColumnVisible = (key) => !hiddenColumns.includes(key);
  const toggleColumn = (key) => {
    setHiddenColumns(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  
  // Session storage key per user
  const sessionKey = `ops_filters_${user?.id || user?.email || 'default'}`;
  
  // Archive filter
  const [showArchived, setShowArchived] = useState(false);
  
  // Cancel modal state
  const [cancelModal, setCancelModal] = useState({ open: false, operationId: null });
  const [cancelReason, setCancelReason] = useState('');

  // Restore filters from sessionStorage on mount
  const [filtersRestored, setFiltersRestored] = useState(false);
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(sessionKey);
      if (saved) {
        const f = JSON.parse(saved);
        if (f.searchQuery) setSearchQuery(f.searchQuery);
        if (f.statusFilter) setStatusFilter(f.statusFilter);
        if (f.operatorFilter) setOperatorFilter(f.operatorFilter);
        if (f.routeTypeFilter) setRouteTypeFilter(f.routeTypeFilter);
        if (f.companyFilter) setCompanyFilter(f.companyFilter);
        if (f.sortField) setSortField(f.sortField);
        if (f.sortDirection) setSortDirection(f.sortDirection);
        if (f.hiddenColumns) setHiddenColumns(f.hiddenColumns);
        if (f.showArchived !== undefined) setShowArchived(f.showArchived);
      }
    } catch (e) { /* ignore */ }
    setFiltersRestored(true);
  }, [sessionKey]);

  // Auto-apply filter from URL query params (?financialStatus=no_status, ?status=facturation, etc.)
  useEffect(() => {
    if (!filtersRestored) return;
    const params = new URLSearchParams(location.search);
    const finQ = params.get('financialStatus');
    const statusQ = params.get('status');
    const divQ = params.get('divergence');
    if (finQ) setFinancialStatusFilter(finQ);
    if (statusQ) setStatusFilter(statusQ);
    if (divQ === '1' || divQ === 'true') setDivergenceFilter(true);
  }, [location.search, filtersRestored]);
  
  // Save filters to sessionStorage on change
  useEffect(() => {
    if (!filtersRestored) return;
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify({
        searchQuery, statusFilter, operatorFilter, routeTypeFilter, companyFilter, financialStatusFilter,
        sortField, sortDirection, hiddenColumns, showArchived
      }));
    } catch (e) { /* ignore */ }
  }, [searchQuery, statusFilter, operatorFilter, routeTypeFilter, companyFilter, financialStatusFilter, divergenceFilter, sortField, sortDirection, hiddenColumns, showArchived, sessionKey, filtersRestored]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Bulk selection state
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk modals
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  
  // Modal création d'opération
  const [showCreateOperationModal, setShowCreateOperationModal] = useState(false);

  // Advanced search - stores pre-filtered results from AdvancedSearchPanel
  const [advancedFilteredOps, setAdvancedFilteredOps] = useState(null);

  // Expanded operations (for showing children)
  const [expandedOperations, setExpandedOperations] = useState({});
  const [childrenCache, setChildrenCache] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});

  const fetchOperations = useCallback(async () => {
    // Vérifier si l'utilisateur est connecté
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      // Limit haut (5000) pour KPIs corrects sur toute la base, pas seulement la page 1
      const params = new URLSearchParams({ limit: '5000' });
      if (showArchived) params.set('show_archived', 'true');
      const response = await axios.get(`${API}/transport-requests?${params.toString()}`);
      setOperations(response.data);
      setFilteredOperations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching operations:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les opérations",
        variant: "destructive"
      });
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  useEffect(() => {
    // Filter operations - start from advanced-filtered results if active, else all operations
    let filtered = advancedFilteredOps !== null ? advancedFilteredOps : operations;

    if (statusFilter !== 'all') {
      // Phase-based filtering — utilise la fonction canonique phaseOfOp
      if (['preparation', 'execution', 'facturation', 'terminee'].includes(statusFilter)) {
        filtered = filtered.filter(op => phaseOfOp(op) === statusFilter);
      } else {
        // Direct status filter (for cancelled, etc.)
        filtered = filtered.filter(op => op.status === statusFilter);
      }
    }

    // Filter by operator
    if (operatorFilter !== 'all') {
      if (operatorFilter === 'unassigned') {
        filtered = filtered.filter(op => !op.assignedOperatorId);
      } else {
        filtered = filtered.filter(op => op.assignedOperatorId === operatorFilter);
      }
    }

    // Filter by route type (Intraland / Hinterland) — normalisé (gère le legacy INTERLAND)
    if (routeTypeFilter !== 'all') {
      filtered = filtered.filter(op => {
        const t = String(op.intraInterLand || 'INTRALAND').toUpperCase();
        return routeTypeFilter === 'INTRALAND' ? t === 'INTRALAND' : t !== 'INTRALAND';
      });
    }

    // Filter by company (entreprise)
    if (companyFilter !== 'all') {
      if (companyFilter === 'particulier') {
        filtered = filtered.filter(op => !op.companyName);
      } else {
        filtered = filtered.filter(op => op.companyName === companyFilter);
      }
    }

    // Filter by financial status
    if (financialStatusFilter !== 'all') {
      if (financialStatusFilter === 'no_status') {
        filtered = filtered.filter(op => !op.financialStatus);
      } else {
        filtered = filtered.filter(op => op.financialStatus === financialStatusFilter);
      }
    }

    // Filter: Cohérence Statut × Facturation (SPOT only, non-legacy)
    // Règles de cohérence simplifiées, miroir de backend divergence_detector.py
    if (divergenceFilter) {
      const COHERENT = {
        "demande_enregistree": [null, undefined, "pending", "quote_requested"],
        "en_attente_de_devis": [null, undefined, "pending", "quote_requested"],
        "en_attente_validation_client": [null, undefined, "pending", "quote_sent"],
        "devis_accepte": ["quote_accepted", "pending"],
        "planifiee": ["quote_accepted", "pending"],
        "prete_au_chargement": ["quote_accepted"],
        "en_cours_de_chargement": ["quote_accepted"],
        "charge_en_route": ["quote_accepted"],
        "livree": ["quote_accepted", "invoiced", "pending_payment", "pending"],
        "en_attente_paiement": ["invoiced", "pending_payment", "partially_paid"],
        "payee": ["paid"],
        "terminee": ["paid"],
      };
      filtered = filtered.filter((op) => {
        if (op.linkedContractId || op.isLegacyImport) return false;
        const allowed = COHERENT[op.status];
        if (!allowed) return false;
        return !allowed.includes(op.financialStatus || null);
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(op =>
        op.departure?.toLowerCase().includes(query) ||
        op.arrival?.toLowerCase().includes(query) ||
        op.email?.toLowerCase().includes(query) ||
        op.id?.toLowerCase().includes(query) ||
        (op.operationNumber && op.operationNumber.toLowerCase().includes(query)) ||
        // Recherche par référence externe client (N° dossier, N° commande…)
        (op.externalRef && op.externalRef.toLowerCase().includes(query)) ||
        (op.assignedOperatorName && op.assignedOperatorName.toLowerCase().includes(query)) ||
        // Recherche par entreprise
        (op.companyName && op.companyName.toLowerCase().includes(query)) ||
        // Recherche par membre (nom/prénom du client)
        (op.firstName && op.firstName.toLowerCase().includes(query)) ||
        (op.lastName && op.lastName.toLowerCase().includes(query)) ||
        // Recherche combinée nom complet
        (`${op.firstName || ''} ${op.lastName || ''}`.toLowerCase().includes(query))
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      let valA, valB;
      const dir = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'reference':
          valA = (a.operationNumber || a.id || '').toLowerCase();
          valB = (b.operationNumber || b.id || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'externalRef':
          valA = (a.externalRef || '').toLowerCase();
          valB = (b.externalRef || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'client':
          valA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          valB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'entreprise':
          valA = (a.companyName || '').toLowerCase();
          valB = (b.companyName || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'date':
          valA = new Date(a.deliveryDate || a.requestedDate || a.pickup_date || a.createdAt || 0).getTime();
          valB = new Date(b.deliveryDate || b.requestedDate || b.pickup_date || b.createdAt || 0).getTime();
          return (valA - valB) * dir;
        case 'poids':
          valA = a.weight || 0;
          valB = b.weight || 0;
          return (valA - valB) * dir;
        case 'distance':
          valA = a.distance_km || 0;
          valB = b.distance_km || 0;
          return (valA - valB) * dir;
        case 'prix':
          valA = a.estimatedPrice || 0;
          valB = b.estimatedPrice || 0;
          return (valA - valB) * dir;
        case 'statut':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'statutFinancier':
          valA = (a.financialStatus || '').toLowerCase();
          valB = (b.financialStatus || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'operateur':
          valA = (a.assignedOperatorName || a.operatorName || '').toLowerCase();
          valB = (b.assignedOperatorName || b.operatorName || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'commercial':
          valA = (a.assignedCommercialName || '').toLowerCase();
          valB = (b.assignedCommercialName || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'transporteur':
          valA = (a.partnerName || a.transportPartnerName || a.carrierName || '').toLowerCase();
          valB = (b.partnerName || b.transportPartnerName || b.carrierName || '').toLowerCase();
          return valA.localeCompare(valB) * dir;
        case 'tarifTransport':
          valA = a.carrierPrice || 0;
          valB = b.carrierPrice || 0;
          return (valA - valB) * dir;
        case 'marge':
          valA = (a.estimatedPrice || 0) - (a.carrierPrice || 0);
          valB = (b.estimatedPrice || 0) - (b.carrierPrice || 0);
          return (valA - valB) * dir;
        case 'createdAt':
        default:
          valA = new Date(a.createdAt || a.requestedDate || 0).getTime();
          valB = new Date(b.createdAt || b.requestedDate || 0).getTime();
          return (valA - valB) * dir;
      }
    });

    setFilteredOperations(filtered);
    
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchQuery, statusFilter, operatorFilter, routeTypeFilter, companyFilter, financialStatusFilter, operations, advancedFilteredOps, sortField, sortDirection]);

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOperations([]);
      setSelectAll(false);
    } else {
      setSelectedOperations(filteredOperations.map(op => op.id));
      setSelectAll(true);
    }
  };

  const handleSelectOperation = (operationId) => {
    if (selectedOperations.includes(operationId)) {
      setSelectedOperations(selectedOperations.filter(id => id !== operationId));
      setSelectAll(false);
    } else {
      const newSelection = [...selectedOperations, operationId];
      setSelectedOperations(newSelection);
      if (newSelection.length === filteredOperations.length) {
        setSelectAll(true);
      }
    }
  };

  const clearSelection = () => {
    setSelectedOperations([]);
    setSelectAll(false);
  };

  // Bulk actions handlers
  const handleBulkAssignOperator = () => {
    setShowOperatorModal(true);
  };

  const handleBulkCancel = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler ${selectedOperations.length} opération(s) ?`)) {
      return;
    }

    const reason = window.prompt('Raison de l\'annulation (obligatoire) :');
    if (!reason) {
      toast({
        title: "Annulation requise",
        description: "Vous devez fournir une raison pour l'annulation",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/bulk-actions/change-status`, {
        operationIds: selectedOperations,
        newStatus: 'annulee_admin',
        reason: reason
      });

      toast({
        title: "✅ Opérations annulées",
        description: `${response.data.success_count} opération(s) annulée(s)`
      });

      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'annulation",
        variant: "destructive"
      });
    }
  };

  const handleBulkPayment = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/change-status`, {
        operationIds: selectedOperations,
        newStatus: 'payee'
      });

      toast({
        title: "✅ Opérations marquées comme payées",
        description: `${response.data.success_count} opération(s) mise(s) à jour`
      });

      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la mise à jour",
        variant: "destructive"
      });
    }
  };

  const handleBulkComplete = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/change-status`, {
        operationIds: selectedOperations,
        newStatus: 'terminee'
      });

      toast({
        title: "✅ Opérations terminées",
        description: `${response.data.success_count} opération(s) terminée(s)`
      });

      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la clôture",
        variant: "destructive"
      });
    }
  };

  const handleReclassify = async () => {
    try {
      setReclassifying(true);
      const preview = await axios.post(`${API}/operations/reclassify-all`, { dry_run: true });
      const { total, changed, transitions } = preview.data;
      if (!changed) {
        toast({ title: 'Classification à jour', description: `Aucun changement (${total} opérations).` });
        return;
      }
      const lines = Object.entries(transitions).map(([k, v]) => `• ${k} : ${v}`).join('\n');
      const ok = window.confirm(
        `Reclassification INTRALAND / HINTERLAND\n\n${changed} opération(s) sur ${total} seront mises à jour :\n${lines}\n\nAppliquer ?`
      );
      if (!ok) return;
      const res = await axios.post(`${API}/operations/reclassify-all`, { dry_run: false });
      toast({ title: '✅ Reclassification appliquée', description: `${res.data.changed} opération(s) mises à jour.` });
      fetchOperations();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Reclassification impossible', variant: 'destructive' });
    } finally {
      setReclassifying(false);
    }
  };


  // Export : par défaut toute la vue affichée (filtres inclus) ; si des lignes
  // sont cochées, l'export se limite à cette sélection.
  const exportIds = selectedOperations.length > 0
    ? selectedOperations
    : filteredOperations.map(op => op.id);
  const exportScopeLabel = selectedOperations.length > 0
    ? `${selectedOperations.length} ligne(s) sélectionnée(s)`
    : `Vue affichée — ${filteredOperations.length} opération(s)`;

  const exportDisplayedOperations = async (format, columns = 'standard') => {
    if (exportIds.length === 0) {
      toast({ title: 'Aucune opération à exporter', variant: 'destructive' });
      return;
    }
    try {
      await downloadExportPost(`${API}/export/operations/selection`, {
        format,
        filenameBase: columns === 'finance' ? 'BaobabConnect_Operations_Finance' : 'BaobabConnect_Operations',
        payload: { operationIds: exportIds, columns },
        axiosConfig: { headers: { Authorization: 'Bearer cookie-auth' } },
      });
      toast({
        title: format === 'xlsx' ? 'Export Excel lancé' : 'Export CSV lancé',
        description: exportScopeLabel,
      });
    } catch {
      toast({ title: 'Erreur export', variant: 'destructive' });
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/export`, {
        operationIds: selectedOperations,
        format: 'csv'
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `operations_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: "✅ Export réussi",
        description: `${selectedOperations.length} opération(s) exportée(s)`
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de l'export",
        variant: "destructive"
      });
    }
  };

  const handleSendPaymentReminder = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir envoyer un rappel de paiement à ${selectedOperations.length} client(s) ?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/bulk-actions/send-payment-reminder`, {
        operationIds: selectedOperations
      });

      toast({
        title: "✅ Rappels envoyés",
        description: `${response.data.success_count} rappel(s) de paiement envoyé(s)`
      });

      if (response.data.failed_count > 0) {
        console.warn('Failed operations:', response.data.failed_operations);
      }

      clearSelection();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'envoi des rappels",
        variant: "destructive"
      });
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(
      `Déplacer ${selectedOperations.length} opération(s) dans la corbeille ? Vous pourrez les restaurer pendant 90 jours.`
    )) return;
    try {
      const response = await axios.post(`${API}/bulk-actions/delete`, { operationIds: selectedOperations });
      toast({ title: "Opérations déplacées dans la corbeille", description: `${response.data.success_count} opération(s) dans la corbeille` });
      if (response.data.failed_count > 0) {
        toast({ title: "Certaines suppressions ont échoué", description: `${response.data.failed_count} opération(s) non supprimée(s)`, variant: "destructive" });
      }
      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de la suppression", variant: "destructive" });
    }
  };

  const handleRestoreFromCancelled = async (operationId) => {
    if (!window.confirm("Restaurer cette opération annulée ? Elle retrouvera son statut précédent.")) return;
    try {
      const resp = await axios.post(`${API}/transport-requests/${operationId}/restore-from-cancelled`);
      toast({
        title: "Opération restaurée",
        description: resp.data?.warning
          ? `Statut: ${resp.data.newStatus}. ⚠️ ${resp.data.warning.message}`
          : `Nouveau statut: ${resp.data.newStatus}${resp.data.autoUnarchived ? ' (auto-désarchivée)' : ''}`,
      });
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de restaurer l'opération", variant: "destructive" });
    }
  };

  const handleBulkRestoreFromCancelled = async () => {
    if (!window.confirm(`Restaurer ${selectedOperations.length} opération(s) annulée(s) ?`)) return;
    try {
      const resp = await axios.post(`${API}/bulk-actions/restore-from-cancelled`, { operationIds: selectedOperations });
      toast({ title: "Restauration effectuée", description: `${resp.data.success_count} opération(s) restaurée(s)` + (resp.data.warnings?.length ? ` (${resp.data.warnings.length} avec factures finalisées)` : '') });
      if (resp.data.failed_count > 0) {
        toast({ title: "Certaines restaurations ont échoué", description: `${resp.data.failed_count} échec(s)`, variant: "destructive" });
      }
      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de la restauration", variant: "destructive" });
    }
  };

  const handleBulkUnarchive = async () => {
    if (!window.confirm(`Désarchiver ${selectedOperations.length} opération(s) ?`)) return;
    try {
      const resp = await axios.post(`${API}/bulk-actions/unarchive`, { operationIds: selectedOperations });
      toast({ title: "Désarchivage effectué", description: `${resp.data.success_count} opération(s) désarchivée(s)` });
      if (resp.data.failed_count > 0) {
        toast({ title: "Certains désarchivages ont échoué", description: `${resp.data.failed_count} échec(s)`, variant: "destructive" });
      }
      clearSelection();
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors du désarchivage", variant: "destructive" });
    }
  };


  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <Badge className={`${config.bgColor} ${config.textColor} border-0`}>
        {config.icon} {config.shortLabel}
      </Badge>
    );
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-[#1db584]" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-[#1db584]" />;
  };

  // Reset all filters
  const advancedSearchRef = React.useRef(null);
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setOperatorFilter('all');
    setRouteTypeFilter('all');
    setCompanyFilter('all');
    setSortField('createdAt');
    setSortDirection('desc');
    setShowArchived(false);
    setAdvancedFilteredOps(null);
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || operatorFilter !== 'all' || routeTypeFilter !== 'all' || companyFilter !== 'all' || showArchived || advancedFilteredOps !== null;

  // Fetch children for a parent operation
  const fetchChildren = async (parentId) => {
    if (childrenCache[parentId]) {
      return childrenCache[parentId];
    }
    
    setLoadingChildren(prev => ({ ...prev, [parentId]: true }));
    try {
      const response = await axios.get(`${API}/operations/${parentId}/children`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      const children = response.data.items || [];
      setChildrenCache(prev => ({ ...prev, [parentId]: children }));
      return children;
    } catch (error) {
      console.error('Error fetching children:', error);
      return [];
    } finally {
      setLoadingChildren(prev => ({ ...prev, [parentId]: false }));
    }
  };

  // Toggle expand/collapse for an operation
  const toggleExpand = async (e, opId) => {
    e.stopPropagation();
    
    if (expandedOperations[opId]) {
      setExpandedOperations(prev => ({ ...prev, [opId]: false }));
    } else {
      await fetchChildren(opId);
      setExpandedOperations(prev => ({ ...prev, [opId]: true }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const handleArchiveOperation = async (operationId) => {
    if (!window.confirm('Archiver cette opération ? Elle ne sera plus visible par défaut.')) return;
    try {
      await axios.post(`${API}/transport-requests/${operationId}/archive`);
      toast({ title: "Opération archivée", description: "L'opération a été archivée avec succès" });
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible d'archiver l'opération", variant: "destructive" });
    }
  };

  const handleUnarchiveOperation = async (operationId) => {
    try {
      await axios.post(`${API}/transport-requests/${operationId}/unarchive`);
      toast({ title: "Opération désarchivée", description: "L'opération a été désarchivée avec succès" });
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de désarchiver l'opération", variant: "destructive" });
    }
  };

  const handleDeleteOperation = async (operationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette opération ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/transport-requests/${operationId}`);
      toast({ title: "Opération supprimée", description: "L'opération a été supprimée avec succès" });
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de supprimer l'opération", variant: "destructive" });
    }
  };

  const handleCancelOperation = async () => {
    if (!cancelReason.trim()) {
      toast({ title: "Raison obligatoire", description: "Veuillez fournir une raison pour l'annulation.", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${API}/transport-requests/${cancelModal.operationId}/change-status?new_status=annulee_admin&reason=${encodeURIComponent(cancelReason.trim())}`);
      toast({ title: "Opération annulée", description: "L'opération a été annulée avec succès" });
      setCancelModal({ open: false, operationId: null });
      setCancelReason('');
      fetchOperations();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible d'annuler l'opération", variant: "destructive" });
    }
  };

  // Dupliquer une opération
  const handleDuplicateOperation = async (operationId) => {
    try {
      await axios.post(`${API}/transport-requests/${operationId}/duplicate`);
      toast({
        title: "✅ Opération dupliquée",
        description: "L'opération a été dupliquée avec succès"
      });
      fetchOperations();
    } catch (error) {
      console.error('Error duplicating operation:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de dupliquer l'opération",
        variant: "destructive"
      });
    }
  };

  // Stats by phase - based on FILTERED operations
  const preparationStatuses = ['demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client', 'en_negociation', 'devis_accepte'];
  const executionStatuses = ['planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree'];
  const facturationStatuses = ['en_attente_paiement', 'payee'];

  // Helpers enrichissement
  const _now = new Date();
  const _in7days = new Date(_now.getTime() + 7 * 24 * 3600 * 1000);
  const _parseDate = (d) => {
    if (!d) return null;
    try { const dt = new Date(d); return isNaN(dt) ? null : dt; } catch { return null; }
  };

  const _prepOps = filteredOperations.filter(op => phaseOfOp(op) === 'preparation');
  const _execOps = filteredOperations.filter(op => phaseOfOp(op) === 'execution');
  const _factOps = filteredOperations.filter(op => phaseOfOp(op) === 'facturation');
  const _termOps = filteredOperations.filter(op => phaseOfOp(op) === 'terminee');

  const stats = {
    total: filteredOperations.length,
    totalAll: operations.length,
    preparation: _prepOps.length,
    execution: _execOps.length,
    facturation: _factOps.length,
    terminee: _termOps.length,
    // Sub-metrics : alertes actionnables uniquement (montants retirés — demande utilisateur 23/04/2026)
    prepUrgent: _prepOps.filter(op => { const dt = _parseDate(op.deliveryDate); return dt && dt <= _in7days; }).length,
    execLate: _execOps.filter(op => { const dt = _parseDate(op.deliveryDate); return dt && dt < _now; }).length,
    factLate: _factOps.filter(op => { const dt = _parseDate(op.dueDate || op.deliveryDate); return dt && dt < _now; }).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedOperations.length}
        onClearSelection={clearSelection}
        onAssignOperator={handleBulkAssignOperator}
        onBulkCancel={handleBulkCancel}
        onBulkPayment={handleBulkPayment}
        onBulkComplete={handleBulkComplete}
        onExport={handleBulkExport}
        onSendPaymentReminder={handleSendPaymentReminder}
        onBulkDelete={handleBulkDelete}
        onBulkRestoreFromCancelled={
          selectedOperations.length > 0 && selectedOperations.every(id => {
            const op = operations.find(o => o.id === id);
            return op && ['annulee_admin', 'annulee_par_client'].includes(op.status) && !op.isArchived && !op.isDeleted;
          }) ? handleBulkRestoreFromCancelled : null
        }
        onBulkUnarchive={
          selectedOperations.length > 0 && selectedOperations.every(id => {
            const op = operations.find(o => o.id === id);
            return op && op.isArchived === true && !op.isDeleted;
          }) ? handleBulkUnarchive : null
        }
      />

      {/* Operator Assignment Modal */}
      <OperatorAssignmentModal
        open={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        operationIds={selectedOperations}
        onSuccess={() => {
          fetchOperations();
          clearSelection();
          setShowOperatorModal(false);
        }}
      />

      {/* Modal création d'opération pour un client */}
      <AdminCreateOperationModal
        isOpen={showCreateOperationModal}
        onClose={() => setShowCreateOperationModal(false)}
        onSuccess={() => {
          fetchOperations();
          setShowCreateOperationModal(false);
        }}
      />

      {/* LOT B — Alerte pré-facturation : références client manquantes */}
      <MissingExternalRefAlert onOpenOperation={(op) => navigate(`/admin/operations/${op.id}`)} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Opérations</h1>
          <p className="text-gray-500 mt-1 text-sm">Suivre et gérer toutes les opérations de transport</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDelete && (
          <Button
            variant="outline"
            onClick={() => navigate('/admin/operations/corbeille')}
            data-testid="open-trash-btn"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Corbeille
          </Button>
          )}
          {canDelete && (
          <Button
            variant="outline"
            onClick={handleReclassify}
            disabled={reclassifying}
            data-testid="reclassify-operations-btn"
            title="Recalcule INTRALAND / HINTERLAND de toutes les opérations"
          >
            <GitCommitHorizontal className="w-4 h-4 mr-2" />
            {reclassifying ? 'Analyse…' : 'Reclassifier'}
          </Button>
          )}
          {canBulkImport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="import-operations-csv-btn">
                <Upload className="w-4 h-4 mr-2" />
                Importer CSV
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuItem onClick={() => setShowCSVImport(true)} data-testid="import-operations-create-btn">
                <ClipboardList className="w-4 h-4 mr-2 text-gray-500" />
                <span className="flex flex-col">
                  <span>Créer des opérations</span>
                  <span className="text-xs text-slate-400">Import classique depuis un CSV</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowRefImport(true)} data-testid="import-external-refs-btn">
                <Hash className="w-4 h-4 mr-2 text-emerald-600" />
                <span className="flex flex-col">
                  <span>Références client (historique)</span>
                  <span className="text-xs text-slate-400">Rattacher les références aux opérations existantes</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {canExport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-csv-dropdown-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs text-gray-500">
                {exportScopeLabel}
              </DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="export-operations-xlsx-btn"
                onClick={() => exportDisplayedOperations('xlsx', 'standard')}
              >
                <ClipboardList className="w-4 h-4 mr-2 text-emerald-600" />
                Exporter en Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-operations-csv-btn"
                onClick={() => exportDisplayedOperations('csv', 'standard')}
              >
                <ClipboardList className="w-4 h-4 mr-2 text-gray-500" />
                Exporter en CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuLabel className="text-xs text-gray-500">Finance (marges, recouvrement)</DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="export-operations-finance-xlsx-btn"
                onClick={() => exportDisplayedOperations('xlsx', 'finance')}
              >
                <CreditCard className="w-4 h-4 mr-2 text-emerald-600" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-operations-finance-btn"
                onClick={() => exportDisplayedOperations('csv', 'finance')}
              >
                <CreditCard className="w-4 h-4 mr-2 text-amber-600" />
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-operations-custom-btn"
                onClick={() => setShowCustomExport(true)}
              >
                <Settings className="w-4 h-4 mr-2 text-purple-600" />
                Personnaliser les colonnes...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-gray-500">Base complète (toutes les opérations)</DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="export-all-operations-xlsx-btn"
                onClick={async () => {
                  try {
                    await downloadExport(`${API}/export/operations`, { format: 'xlsx', filenameBase: 'BaobabConnect_Operations_Toutes', axiosConfig: { headers: { Authorization: 'Bearer cookie-auth' } } });
                    toast({ title: 'Export Excel lancé', description: 'Toutes les opérations' });
                  } catch { toast({ title: 'Erreur export', variant: 'destructive' }); }
                }}
              >
                <Download className="w-4 h-4 mr-2 text-gray-500" />
                Excel — toutes les opérations
              </DropdownMenuItem>
              <DropdownMenuLabel className="text-xs text-gray-500">Export production (réimportable)</DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="export-prod-operations-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/export/production/operations`, { responseType: 'blob'});
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'import_operations.csv');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch { toast({ title: 'Erreur export', variant: 'destructive' }); }
                }}
              >
                <FileDown className="w-4 h-4 mr-2 text-emerald-600" />
                Opérations (60)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-prod-users-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/export/production/users`, { responseType: 'blob'});
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'import_utilisateurs.csv');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch { toast({ title: 'Erreur export', variant: 'destructive' }); }
                }}
              >
                <Users className="w-4 h-4 mr-2 text-blue-600" />
                Utilisateurs liés
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-prod-partners-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/export/production/partners`, { responseType: 'blob'});
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'import_transporteurs.csv');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch { toast({ title: 'Erreur export', variant: 'destructive' }); }
                }}
              >
                <Truck className="w-4 h-4 mr-2 text-orange-600" />
                Transporteurs liés
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {canCreate && (
          <Button 
            onClick={() => setShowCreateOperationModal(true)}
            className="bg-[#1db584] hover:bg-[#18a075] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle opération
          </Button>
          )}
        </div>
      </div>

      {/* Pipeline interactif — 5 cartes cliquables avec flèches séparateurs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] gap-2 items-center">
        {/* Carte TOTAL */}
        <button
          type="button"
          onClick={() => setStatusFilter('all')}
          data-testid="phase-pipeline-total"
          className={`rounded-lg border text-left p-3 h-[88px] transition-all ${
            statusFilter === 'all'
              ? 'ring-2 ring-gray-700 ring-offset-1 border-gray-700 bg-gray-50'
              : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">Total</span>
            {statusFilter === 'all' && <CheckCircle className="w-3 h-3 text-gray-700" />}
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900 tabular-nums leading-none">{stats.total}</span>
            {hasActiveFilters && <span className="text-[10px] text-gray-400">/ {stats.totalAll}</span>}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 truncate">Toutes phases</div>
        </button>
        <ChevronRight className="w-4 h-4 text-gray-300 hidden lg:block justify-self-center" />

        {/* Préparation */}
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'preparation' ? 'all' : 'preparation')}
          data-testid="phase-pipeline-preparation"
          className={`rounded-lg border text-left p-3 h-[88px] transition-all ${
            statusFilter === 'preparation'
              ? 'ring-2 ring-blue-500 ring-offset-1 border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Préparation
            </span>
            {statusFilter === 'preparation' && <CheckCircle className="w-3 h-3 text-blue-600" />}
          </div>
          <div className="mt-0.5 text-2xl font-bold text-blue-600 tabular-nums leading-none">{stats.preparation}</div>
          <div className="mt-1 text-[11px] truncate">
            {stats.prepUrgent > 0 ? (
              <span className="text-amber-600 font-medium">⚠️ {stats.prepUrgent} urgent{stats.prepUrgent > 1 ? 's' : ''} &lt;7j</span>
            ) : (
              <span className="text-gray-500">Devis / planification</span>
            )}
          </div>
        </button>
        <ChevronRight className="w-4 h-4 text-gray-300 hidden lg:block justify-self-center" />

        {/* Exécution */}
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'execution' ? 'all' : 'execution')}
          data-testid="phase-pipeline-execution"
          className={`rounded-lg border text-left p-3 h-[88px] transition-all ${
            statusFilter === 'execution'
              ? 'ring-2 ring-purple-500 ring-offset-1 border-purple-500 bg-purple-50'
              : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <Truck className="w-3 h-3" /> Exécution
            </span>
            {statusFilter === 'execution' && <CheckCircle className="w-3 h-3 text-purple-600" />}
          </div>
          <div className="mt-0.5 text-2xl font-bold text-purple-600 tabular-nums leading-none">{stats.execution}</div>
          <div className="mt-1 text-[11px] truncate">
            {stats.execLate > 0 ? (
              <span className="text-red-600 font-medium">🔴 {stats.execLate} en retard</span>
            ) : (
              <span className="text-gray-500">Transport en cours</span>
            )}
          </div>
        </button>
        <ChevronRight className="w-4 h-4 text-gray-300 hidden lg:block justify-self-center" />

        {/* Facturation */}
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'facturation' ? 'all' : 'facturation')}
          data-testid="phase-pipeline-facturation"
          className={`rounded-lg border text-left p-3 h-[88px] transition-all ${
            statusFilter === 'facturation'
              ? 'ring-2 ring-amber-500 ring-offset-1 border-amber-500 bg-amber-50'
              : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <CreditCard className="w-3 h-3" /> Facturation
            </span>
            {statusFilter === 'facturation' && <CheckCircle className="w-3 h-3 text-amber-600" />}
          </div>
          <div className="mt-0.5 text-2xl font-bold text-amber-600 tabular-nums leading-none">{stats.facturation}</div>
          <div className="mt-1 text-[11px] truncate">
            {stats.factLate > 0 ? (
              <span className="text-red-600 font-medium">🔴 {stats.factLate} échéance dépassée</span>
            ) : (
              <span className="text-gray-500">En attente de paiement</span>
            )}
          </div>
        </button>
        <ChevronRight className="w-4 h-4 text-gray-300 hidden lg:block justify-self-center" />

        {/* Terminées */}
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'terminee' ? 'all' : 'terminee')}
          data-testid="phase-pipeline-terminee"
          className={`rounded-lg border text-left p-3 h-[88px] transition-all ${
            statusFilter === 'terminee'
              ? 'ring-2 ring-green-500 ring-offset-1 border-green-500 bg-green-50'
              : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Terminées
            </span>
            {statusFilter === 'terminee' && <CheckCircle className="w-3 h-3 text-green-600" />}
          </div>
          <div className="mt-0.5 text-2xl font-bold text-green-600 tabular-nums leading-none">{stats.terminee}</div>
          <div className="mt-1 text-[11px] text-gray-500 truncate">Payées / clôturées</div>
        </button>
      </div>

      {/* Advanced Search Panel */}
      <AdvancedSearchPanel
        operations={operations}
        onFilter={(results) => setAdvancedFilteredOps(results)}
        onReset={() => setAdvancedFilteredOps(null)}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Rechercher par entreprise, client, référence, n° dossier, trajet, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {/* Chip d'état phase actif (remplace l'ancien dropdown redondant) */}
              {statusFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setStatusFilter('all')}
                  data-testid="clear-phase-filter"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm text-gray-700 transition-colors w-full sm:w-auto"
                >
                  <span>Phase :</span>
                  <span className="font-medium capitalize">{statusFilter === 'terminee' ? 'Terminée' : statusFilter}</span>
                  <XCircle className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
              {/* Filtre "Annulées" — hors pipeline principal */}
              <Select
                value={statusFilter === 'cancelled' ? 'cancelled' : 'not_cancelled'}
                onValueChange={(v) => setStatusFilter(v === 'cancelled' ? 'cancelled' : 'all')}
              >
                <SelectTrigger className="w-full sm:w-44" data-testid="cancelled-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_cancelled">Actives</SelectItem>
                  <SelectItem value="cancelled"><span className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Annulées</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="w-full sm:w-64">
                <Select value={routeTypeFilter} onValueChange={setRouteTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Type de route" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="INTRALAND">Intraland</SelectItem>
                    <SelectItem value="HINTERLAND">Hinterland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-64">
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrer par entreprise" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les entreprises</SelectItem>
                    <SelectItem value="particulier">Particuliers</SelectItem>
                    {/* Get unique companies from operations */}
                    {Array.from(new Set(operations.filter(op => op.companyName).map(op => op.companyName)))
                      .sort()
                      .map(companyName => (
                        <SelectItem key={companyName} value={companyName}>
                          {companyName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Select value={operatorFilter} onValueChange={setOperatorFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrer par opérateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les opérateurs</SelectItem>
                    <SelectItem value="unassigned">Non assigné</SelectItem>
                    {/* Get unique operators from operations */}
                    {Array.from(new Set(operations.filter(op => op.assignedOperatorId).map(op => op.assignedOperatorId)))
                      .map(operatorId => {
                        const op = operations.find(o => o.assignedOperatorId === operatorId);
                        return (
                          <SelectItem key={operatorId} value={operatorId}>
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {op?.assignedOperatorName || operatorId}</span>
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-56">
                <Select value={financialStatusFilter} onValueChange={setFinancialStatusFilter}>
                  <SelectTrigger data-testid="financial-status-filter">
                    <SelectValue placeholder="Statut financier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts financiers</SelectItem>
                    <SelectItem value="no_status">Sans statut (à qualifier)</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="quote_sent">Devis envoyé</SelectItem>
                    <SelectItem value="quote_accepted">Devis accepté</SelectItem>
                    <SelectItem value="invoiced">Facturée</SelectItem>
                    <SelectItem value="pending_payment">En attente paiement</SelectItem>
                    <SelectItem value="partially_paid">Paiement partiel</SelectItem>
                    <SelectItem value="paid">Payée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Toggle "Afficher uniquement les divergences Statut×Facturation" */}
              <button
                type="button"
                onClick={() => setDivergenceFilter((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm transition-colors ${
                  divergenceFilter
                    ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="divergence-filter-toggle"
                title="Afficher uniquement les opérations en divergence Statut × Facturation (SPOT, non-legacy)"
              >
                <GitCommitHorizontal className="w-4 h-4" />
                Divergences
                {divergenceFilter && (
                  <span className="ml-1 bg-white/25 px-1.5 rounded text-xs">actif</span>
                )}
              </button>
              {/* Column visibility toggle */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="default" data-testid="columns-toggle-btn">
                    <Columns className="w-4 h-4 mr-2" />
                    Colonnes
                    {hiddenColumns.length > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs px-1.5 py-0">{ALL_COLUMNS.length - hiddenColumns.length}/{ALL_COLUMNS.length}</Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-gray-500">Colonnes visibles</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map(col => (
                    <DropdownMenuItem key={col.key} onSelect={(e) => e.preventDefault()} onClick={() => toggleColumn(col.key)} className="cursor-pointer">
                      <Checkbox checked={isColumnVisible(col.key)} className="mr-2" />
                      {col.label}
                    </DropdownMenuItem>
                  ))}
                  {hiddenColumns.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setHiddenColumns([])} className="text-[#1db584] cursor-pointer">
                        Afficher toutes
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Show archived toggle */}
              <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border rounded-md hover:bg-gray-50 text-sm" data-testid="show-archived-toggle">
                <Checkbox
                  checked={showArchived}
                  onCheckedChange={(checked) => setShowArchived(!!checked)}
                />
                <Archive className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700">Archivées</span>
              </label>
              {/* Reset filters button */}
              {hasActiveFilters && (
                <Button variant="ghost" size="default" onClick={resetFilters} data-testid="reset-filters-btn" className="text-gray-500 hover:text-gray-700">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Operations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Opérations ({filteredOperations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                {isColumnVisible('reference') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('reference')} data-testid="sort-reference">
                    <span className="flex items-center">Référence<SortIcon field="reference" /></span>
                  </TableHead>
                )}
                {isColumnVisible('externalRef') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('externalRef')} data-testid="sort-external-ref">
                    <span className="flex items-center">Réf. externe<SortIcon field="externalRef" /></span>
                  </TableHead>
                )}
                {isColumnVisible('client') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('client')} data-testid="sort-client">
                    <span className="flex items-center">Client<SortIcon field="client" /></span>
                  </TableHead>
                )}
                {isColumnVisible('entreprise') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('entreprise')} data-testid="sort-entreprise">
                    <span className="flex items-center">Entreprise<SortIcon field="entreprise" /></span>
                  </TableHead>
                )}
                {isColumnVisible('trajet') && (
                  <TableHead>Trajet</TableHead>
                )}
                {isColumnVisible('distance') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50 text-right w-[90px]" onClick={() => handleSort('distance')} data-testid="sort-distance">
                    <span className="flex items-center justify-end">Distance<SortIcon field="distance" /></span>
                  </TableHead>
                )}
                {isColumnVisible('date') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('date')} data-testid="sort-date">
                    <span className="flex items-center">Date<SortIcon field="date" /></span>
                  </TableHead>
                )}
                {isColumnVisible('poids') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('poids')} data-testid="sort-poids">
                    <span className="flex items-center">Poids<SortIcon field="poids" /></span>
                  </TableHead>
                )}
                {isColumnVisible('prix') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('prix')} data-testid="sort-prix">
                    <span className="flex items-center">Prix<SortIcon field="prix" /></span>
                  </TableHead>
                )}
                {isColumnVisible('statut') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('statut')} data-testid="sort-statut">
                    <span className="flex items-center">Statut<SortIcon field="statut" /></span>
                  </TableHead>
                )}
                {isColumnVisible('statutFinancier') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('statutFinancier')} data-testid="sort-statut-financier">
                    <span className="flex items-center">Statut financier<SortIcon field="statutFinancier" /></span>
                  </TableHead>
                )}
                {isColumnVisible('operateur') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('operateur')} data-testid="sort-operateur">
                    <span className="flex items-center">Opérateur<SortIcon field="operateur" /></span>
                  </TableHead>
                )}
                {isColumnVisible('commercial') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('commercial')} data-testid="sort-commercial">
                    <span className="flex items-center">Commercial<SortIcon field="commercial" /></span>
                  </TableHead>
                )}
                {isColumnVisible('transporteur') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('transporteur')} data-testid="sort-transporteur">
                    <span className="flex items-center">Transporteur<SortIcon field="transporteur" /></span>
                  </TableHead>
                )}
                {isColumnVisible('tarifTransport') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('tarifTransport')} data-testid="sort-tarif">
                    <span className="flex items-center">Tarif Transport<SortIcon field="tarifTransport" /></span>
                  </TableHead>
                )}
                {isColumnVisible('marge') && (
                  <TableHead className="cursor-pointer select-none hover:bg-gray-50" onClick={() => handleSort('marge')} data-testid="sort-marge">
                    <span className="flex items-center">Marge<SortIcon field="marge" /></span>
                  </TableHead>
                )}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOperations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={ALL_COLUMNS.filter(c => isColumnVisible(c.key)).length + 2} className="text-center py-8 text-gray-500">
                    Aucune opération trouvée
                  </TableCell>
                </TableRow>
              ) : (
                (() => {
                  // Pagination logic
                  const indexOfLastItem = currentPage * itemsPerPage;
                  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                  const currentOperations = filteredOperations.slice(indexOfFirstItem, indexOfLastItem);
                  
                  return currentOperations.map((op) => (
                  <React.Fragment key={op.id}>
                  <TableRow 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => op.isParentOperation && op.contractId ? navigate(`/admin/contracts/${op.contractId}`) : navigate(`/admin/operations/${op.id}`)}
                  >
                    <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {/* Expand/Collapse button for parent operations */}
                        {(op.isParentOperation || op.childrenCount > 0) ? (
                          <button 
                            onClick={(e) => toggleExpand(e, op.id)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {loadingChildren[op.id] ? (
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                            ) : expandedOperations[op.id] ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        ) : (
                          <div className="w-6" />
                        )}
                        <Checkbox
                          checked={selectedOperations.includes(op.id)}
                          onCheckedChange={() => handleSelectOperation(op.id)}
                        />
                      </div>
                    </TableCell>
                    {isColumnVisible('reference') && (
                    <TableCell className="font-mono text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-gray-600">{op.operationNumber || op.id.substring(0, 8)}</span>
                          {(op.isParentOperation || op.childrenCount > 0) && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {op.childrenCount || 0}
                            </span>
                          )}
                          {/* ✨ Badge Aller-retour unifié */}
                          {op.tripType === 'round_trip' && (
                            <RoundTripBadge
                              distanceKm={op.distance_km}
                              returnCargoMode={op.returnCargoMode}
                            />
                          )}
                        </div>
                        {op.externalRef && !isColumnVisible('externalRef') && (
                          <span className="text-[11px] text-emerald-700" title={op.externalRefLabel || 'Référence externe client'} data-testid={`admin-ext-ref-${op.id}`}>
                            {op.externalRef}
                          </span>
                        )}
                        {op.intraInterLand && (
                          <span className="text-xs text-gray-500">
                            {op.intraInterLand}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    )}
                    {isColumnVisible('externalRef') && (
                    <TableCell className="font-mono text-xs">
                      {op.externalRef ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSearchQuery(op.externalRef); }}
                          title={`${op.externalRefLabel || 'Référence externe'} — cliquer pour voir toutes les opérations de ce dossier`}
                          className="text-emerald-700 hover:text-emerald-900 hover:underline"
                          data-testid={`admin-ext-ref-cell-${op.id}`}
                        >
                          {op.externalRef}
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('client') && (
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{op.firstName} {op.lastName}</p>
                        <p className="text-xs text-gray-500">{op.email}</p>
                      </div>
                    </TableCell>
                    )}
                    {isColumnVisible('entreprise') && (
                    <TableCell>
                      {(['entreprise', 'company'].includes(op.accountType) || op.companyName) ? (
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-700 font-medium">{op.companyName || 'Entreprise'}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Particulier</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('trajet') && (
                    <TableCell>
                      {(() => {
                        // PM Spec 03/06/2026 — Format inline "Départ → Étape → Arrivée" (cohérent transferts)
                        const stops = op.stops || [];
                        let cities = stops
                          .map(s => s.city || s.address?.split(',')[0])
                          .filter(Boolean);
                        if (cities.length === 0) {
                          cities = [
                            op.departure?.split(',')[0],
                            op.arrival?.split(',')[0],
                          ].filter(Boolean);
                        }
                        if (cities.length === 0) return <span className="text-gray-400 text-sm">-</span>;
                        const full = cities.join(' → ');
                        return (
                          <div
                            className="text-sm text-gray-700 truncate max-w-[260px]"
                            title={full}
                            data-testid={`op-${op.id}-trajet`}
                          >
                            {full}
                          </div>
                        );
                      })()}
                    </TableCell>
                    )}
                    {isColumnVisible('distance') && (
                    <TableCell className="text-right text-sm font-medium text-gray-600" data-testid={`op-${op.id}-distance`}>
                      {op.distance_km ? `${Math.round(op.distance_km)} km` : <span className="text-gray-300">—</span>}
                    </TableCell>
                    )}
                    {isColumnVisible('date') && (
                    <TableCell className="text-gray-600">{formatDate(op.deliveryDate || op.requestedDate || op.pickup_date)}</TableCell>
                    )}
                    {isColumnVisible('poids') && (
                    <TableCell className="text-gray-600">{op.weight} T</TableCell>
                    )}
                    {isColumnVisible('prix') && (
                    <TableCell className="font-medium text-gray-900">
                      {op.estimatedPrice > 0
                        ? <>{op.estimatedPrice.toLocaleString('fr-FR')} <span className="text-xs font-normal text-gray-500">XOF</span></>
                        : <span className="text-xs text-gray-400">-</span>
                      }
                    </TableCell>
                    )}
                    {isColumnVisible('statut') && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <StatusBadge status={op.status} size="sm" variant="dot" />
                        {op.isArchived && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0 text-amber-600 border-amber-300 bg-amber-50" data-testid={`archived-badge-${op.id}`}>
                            <Archive className="w-3 h-3 mr-0.5" />Archivée
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    )}
                    {isColumnVisible('statutFinancier') && (
                    <TableCell data-testid={`financial-status-${op.id}`}>
                      {op.financialStatus ? (
                        <FinancialStatusBadge status={op.financialStatus} />
                      ) : (
                        <InlineQualifyStatus
                          op={op}
                          onQualified={(opId, newStatus) => {
                            setOperations((prev) => prev.map((o) => (o.id === opId ? { ...o, financialStatus: newStatus } : o)));
                          }}
                        />
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('operateur') && (
                    <TableCell>
                      {(op.assignedOperatorName || op.operatorName) ? (
                        <span className="text-xs text-gray-600 flex items-center gap-1">
                          <User className="w-3 h-3" /> {op.assignedOperatorName || op.operatorName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Non assigné</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('commercial') && (
                    <TableCell>
                      {op.assignedCommercialName ? (
                        <span className="text-xs text-emerald-700 font-medium flex items-center gap-1" data-testid={`op-commercial-${op.id}`}>
                          <User className="w-3 h-3" /> {op.assignedCommercialName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Sans commercial</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('transporteur') && (
                    <TableCell>
                      {(op.partnerName || op.transportPartnerName || op.carrierName) ? (
                        <span className="text-xs text-amber-700 font-medium">{op.partnerName || op.transportPartnerName || op.carrierName}</span>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('tarifTransport') && (
                    <TableCell>
                      {op.carrierPrice > 0 ? (
                        <span className="text-xs text-gray-700">{op.carrierPrice.toLocaleString('fr-FR')} <span className="text-gray-400">XOF</span></span>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    )}
                    {isColumnVisible('marge') && (
                    <TableCell>
                      {op.estimatedPrice > 0 && op.carrierPrice > 0 ? (
                        (() => {
                          const marge = op.estimatedPrice - op.carrierPrice;
                          const pct = ((marge / op.estimatedPrice) * 100).toFixed(0);
                          return (
                            <div className="text-xs">
                              <span className={marge >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                                {marge.toLocaleString('fr-FR')} XOF
                              </span>
                              <span className="text-gray-400 ml-1">({pct}%)</span>
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateOperation(op.id);
                          }}
                          title="Dupliquer cette opération"
                          data-testid={`duplicate-op-${op.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {/* Archive button - admin/commercial only, for terminated/cancelled ops */}
                        {['admin', 'commercial'].includes(user?.role?.toLowerCase()) && ['annulee_admin', 'annulee_par_client', 'terminee', 'completed', 'payee'].includes(op.status) && !op.isArchived && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveOperation(op.id);
                            }}
                            title="Archiver cette opération"
                            data-testid={`archive-op-${op.id}`}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Unarchive button - admin/commercial only, for archived ops */}
                        {['admin', 'commercial'].includes(user?.role?.toLowerCase()) && op.isArchived && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnarchiveOperation(op.id);
                            }}
                            title="Désarchiver cette opération"
                            data-testid={`unarchive-op-${op.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Restore-from-cancelled — for annulee_* ops, admin/lead_ops/lead_commercial */}
                        {['annulee_admin', 'annulee_par_client'].includes(op.status) && !op.isArchived && !op.isDeleted && ['admin', 'lead_ops', 'lead_commercial'].includes(user?.role?.toLowerCase()) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreFromCancelled(op.id);
                            }}
                            title="Restaurer cette opération annulée"
                            data-testid={`restore-cancelled-op-${op.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Cancel button - for non-cancelled, non-terminated, non-archived ops */}
                        {!['annulee_admin', 'annulee_par_client', 'terminee', 'completed'].includes(op.status) && !op.isArchived && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCancelModal({ open: true, operationId: op.id });
                              setCancelReason('');
                            }}
                            title="Annuler cette opération"
                            data-testid={`cancel-op-${op.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Delete button - admin/commercial only */}
                        {['admin', 'commercial'].includes(user?.role?.toLowerCase()) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteOperation(op.id);
                            }}
                            title="Supprimer cette opération"
                            data-testid={`delete-op-${op.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {/* Child Operations (Expanded) */}
                  {expandedOperations[op.id] && childrenCache[op.id]?.map((child) => (
                    <TableRow 
                      key={child.id} 
                      className="bg-gray-50/50 hover:bg-gray-100 cursor-pointer"
                      data-testid={`child-row-${child.id}`}
                      onClick={() => child.contractId
                        ? navigate(`/admin/contracts/${child.contractId}/runs/${child.id}`)
                        : navigate(`/admin/operations/${child.id}`)
                      }
                    >
                      <TableCell className="w-12">
                        <div className="flex items-center gap-1">
                          <div className="w-6" />
                          <div className="w-4 h-4 border-l-2 border-b-2 border-gray-200 ml-2" />
                        </div>
                      </TableCell>
                      {isColumnVisible('reference') && (
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="text-xs">↳</span>
                          <span>{child.runNumber || child.id.slice(0, 8)}</span>
                        </div>
                      </TableCell>
                      )}
                      {isColumnVisible('client') && (
                      <TableCell>
                        <span className="text-xs text-gray-500">
                          {child.inheritedData?.clientName || `${op.firstName} ${op.lastName}`}
                        </span>
                      </TableCell>
                      )}
                      {isColumnVisible('entreprise') && (
                      <TableCell>
                        <span className="text-xs text-gray-400">
                          {op.companyName || ''}
                        </span>
                      </TableCell>
                      )}
                      {isColumnVisible('trajet') && (
                      <TableCell>
                        <div className="text-xs text-gray-500">
                          {(() => {
                            const stops = child.stops || [];
                            const dep = child.inheritedData?.departureAddress || stops[0]?.address || op.departure || '';
                            const arr = child.inheritedData?.arrivalAddress || (stops.length > 0 ? stops[stops.length - 1]?.address : '') || op.arrival || '';
                            return (<>
                              <p>{dep.length > 28 ? dep.slice(0, 28) + '...' : dep}</p>
                              <p>→ {arr.length > 28 ? arr.slice(0, 28) + '...' : arr}</p>
                            </>);
                          })()}
                        </div>
                      </TableCell>
                      )}
                      {isColumnVisible('date') && (
                      <TableCell className="text-xs text-gray-600">
                        {new Date(child.date).toLocaleDateString('fr-FR')}
                        <span className="text-gray-400 ml-1">({child.shift === 'day' ? 'Jour' : 'Nuit'})</span>
                      </TableCell>
                      )}
                      {isColumnVisible('poids') && (
                      <TableCell className="text-xs text-gray-600">
                        {child.weight ? `${child.weight} T` : '-'}
                      </TableCell>
                      )}
                      {isColumnVisible('prix') && (
                      <TableCell className="text-xs text-gray-500">
                        {child.contractBillingMode === 'monthly_fixed' || child.contractBillingMode === 'fleet_location'
                          ? '-'
                          : child.unitPrice > 0 ? child.unitPrice.toLocaleString('fr-FR') : '-'
                        }
                      </TableCell>
                      )}
                      {isColumnVisible('statut') && (
                      <TableCell>
                        <span className={`text-xs font-medium ${
                          child.status === 'completed' ? 'text-gray-700' :
                          child.status === 'in_progress' ? 'text-gray-700' :
                          child.status === 'cancelled' ? 'text-gray-400 line-through' :
                          'text-gray-600'
                        }`}>
                          {child.status === 'completed' ? '• Terminé' :
                           child.status === 'in_progress' ? '• En cours' :
                           child.status === 'cancelled' ? '• Annulé' : '• Planifié'}
                        </span>
                      </TableCell>
                      )}
                      {isColumnVisible('statutFinancier') && (
                      <TableCell><span className="text-xs text-gray-400">—</span></TableCell>
                      )}
                      {isColumnVisible('operateur') && (
                      <TableCell className="text-xs text-gray-500">
                        {child.assignedOperatorName || '-'}
                      </TableCell>
                      )}
                      {isColumnVisible('transporteur') && (
                      <TableCell className="text-xs text-gray-400">
                        {child.partnerName || '-'}
                      </TableCell>
                      )}
                      {isColumnVisible('tarifTransport') && <TableCell />}
                      {isColumnVisible('marge') && <TableCell />}
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                  </React.Fragment>
                  ));
                })()
              )}
            </TableBody>
          </Table>
          
          {/* Pagination Controls */}
          {filteredOperations.length > itemsPerPage && (
            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div className="text-sm text-gray-600">
                Affichage de {Math.min((currentPage - 1) * itemsPerPage + 1, filteredOperations.length)} à{' '}
                {Math.min(currentPage * itemsPerPage, filteredOperations.length)} sur {filteredOperations.length} opérations
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </Button>
                <div className="flex items-center gap-1">
                  {(() => {
                    const totalPages = Math.ceil(filteredOperations.length / itemsPerPage);
                    const pages = [];
                    const maxPagesToShow = 5;
                    
                    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                    
                    if (endPage - startPage < maxPagesToShow - 1) {
                      startPage = Math.max(1, endPage - maxPagesToShow + 1);
                    }
                    
                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          variant={1 === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis-start" className="px-2">...</span>);
                      }
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(i)}
                          className="w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis-end" className="px-2">...</span>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          variant={totalPages === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          className="w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredOperations.length / itemsPerPage)))}
                  disabled={currentPage >= Math.ceil(filteredOperations.length / itemsPerPage)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Operation Modal */}
      {cancelModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="cancel-modal-overlay">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" data-testid="cancel-modal">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Annuler l'opération</h3>
            <p className="text-sm text-gray-500 mb-4">Cette action changera le statut de l'opération en "Annulée". Veuillez fournir une raison.</p>
            <textarea
              className="w-full border rounded-md p-3 text-sm min-h-[100px] focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
              placeholder="Raison de l'annulation (obligatoire)..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancel-reason-input"
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => { setCancelModal({ open: false, operationId: null }); setCancelReason(''); }}
                data-testid="cancel-modal-close-btn"
              >
                Fermer
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancelOperation}
                disabled={!cancelReason.trim()}
                data-testid="cancel-modal-confirm-btn"
              >
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      <CustomExportModal
        open={showCustomExport}
        onClose={() => setShowCustomExport(false)}
        onToast={toast}
      />

      <CSVImportModal
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        type="operations"        columns={[
          { key: 'departure', label: 'Départ' },
          { key: 'arrival', label: 'Arrivée' },
          { key: 'deliveryDate', label: 'Date' },
          { key: 'vehicleType', label: 'Véhicule' },
          { key: 'operationType', label: 'Type op.' },
          { key: 'weight', label: 'Poids (T)' },
          { key: 'merchandiseType', label: 'Nature' },
          { key: 'clientEmail', label: 'Client' },
        ]}
        onSuccess={() => fetchOperations()}
      />

      {/* LOT B2 — Reprise d'historique des références client */}
      <CSVImportModal
        open={showRefImport}
        onOpenChange={setShowRefImport}
        type="external-references"
        columns={[
          { key: 'operationNumber', label: 'N° Opération' },
          { key: 'clientName', label: 'Client' },
          { key: 'currentValue', label: 'Valeur actuelle' },
          { key: 'externalRef', label: 'Nouvelle référence' },
        ]}
        onSuccess={() => fetchOperations()}
      />
    </div>
  );
};

export default OperationsManagement;
